import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';

function buildCredentialsCopyText(data) {
  if (!data) return '';
  const lines = [];
  if (data.portal_url) lines.push(`Portal URL: ${data.portal_url}`);
  if (data.email) lines.push(`Email: ${data.email}`);
  if (data.password) lines.push(`Password: ${data.password}`);
  return lines.join('\n');
}

const CredentialField = ({ label, value, fieldKey, copiedField, onCopy }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    <TextField
      value={value || ''}
      type={fieldKey === 'password' ? 'text' : 'text'}
      fullWidth
      size="small"
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => onCopy(value, fieldKey)} size="small">
              {copiedField === fieldKey ? <Check color="success" /> : <ContentCopy />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  </Box>
);

const CustomerCredentialsDialog = ({
  open,
  customerData,
  onClose,
  title = 'Customer Created Successfully',
  description = 'Please save these credentials for the new customer:',
  note = 'These credentials have been automatically saved. The customer can use them to log into their account.',
}) => {
  const [copiedField, setCopiedField] = React.useState(null);

  const handleCopy = async (text, field) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCopyAll = async () => {
    const text = buildCredentialsCopyText(customerData);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField('all');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy all: ', err);
    }
  };

  const hasCredentials =
    customerData?.portal_url || customerData?.email || customerData?.password;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>

          {hasCredentials ? (
            <>
              {customerData.portal_url && (
                <CredentialField
                  label="Portal URL"
                  value={customerData.portal_url}
                  fieldKey="portal_url"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />
              )}

              {customerData.email && (
                <CredentialField
                  label="Email"
                  value={customerData.email}
                  fieldKey="email"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />
              )}

              {customerData.password && (
                <CredentialField
                  label="Password"
                  value={customerData.password}
                  fieldKey="password"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />
              )}
            </>
          ) : (
            <Typography color="error">No customer data available</Typography>
          )}

          {note && (
            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> {note}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCopyAll}
          variant="outlined"
          disabled={!hasCredentials}
          startIcon={copiedField === 'all' ? <Check color="success" /> : <ContentCopy />}
        >
          {copiedField === 'all' ? 'Copied!' : 'Copy All'}
        </Button>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerCredentialsDialog;
